import { useInView } from '../hooks/useInView';
import './ComparisonSection.css';

const CHECK = (
  <span className="cmp-icon cmp-icon--yes" aria-label="Included">
    <svg width="15" height="15" viewBox="0 0 15 15" fill="none">
      <circle cx="7.5" cy="7.5" r="6.75" stroke="currentColor" strokeWidth="1.25"/>
      <path d="M4.5 7.5l2.5 2.5 3.5-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  </span>
);

const CROSS = (
  <span className="cmp-icon cmp-icon--no" aria-label="Not included">
    <svg width="15" height="15" viewBox="0 0 15 15" fill="none">
      <circle cx="7.5" cy="7.5" r="6.75" stroke="currentColor" strokeWidth="1.25"/>
      <path d="M5 10l5-5M10 10L5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  </span>
);

const PARTIAL = (
  <span className="cmp-icon cmp-icon--partial" aria-label="Varies">
    <svg width="15" height="15" viewBox="0 0 15 15" fill="none">
      <circle cx="7.5" cy="7.5" r="6.75" stroke="currentColor" strokeWidth="1.25"/>
      <path d="M4.5 7.5h6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  </span>
);

const ROWS = [
  {
    category: 'Compliance & Onboarding',
    items: [
      {
        title: 'Drivers submitting docs over email and WhatsApp',
        detail: 'No structured channel — docs arrive scattered, get lost, and are impossible to audit.',
        taxiops: { icon: CHECK, note: 'Driver self-service portal' },
        dispatch: CROSS,
        manual: CROSS,
      },
      {
        title: 'Wet-ink signatures on printed forms',
        detail: 'ICA, lease agreement, violation acknowledgments — all require printing, signing, and scanning.',
        taxiops: { icon: CHECK, note: 'Built-in e-signature, signed PDF stored' },
        dispatch: CROSS,
        manual: CROSS,
      },
      {
        title: 'Licence expires — dispatcher finds out at shift start',
        detail: 'No proactive alert. Driver shows up, gets dispatched. Violation happens.',
        taxiops: { icon: CHECK, note: '5 fields tracked, auto email alerts' },
        dispatch: PARTIAL,
        manual: CROSS,
      },
      {
        title: 'Onboarding takes days of back-and-forth',
        detail: 'Paper forms, in-person visits, chasing missing signatures — delays first shift.',
        taxiops: { icon: CHECK, note: '4-step portal, completed on phone' },
        dispatch: CROSS,
        manual: CROSS,
      },
      {
        title: 'Regulators ask for compliance export — takes hours',
        detail: 'Gathering records from spreadsheets, emails, and filing cabinets under time pressure.',
        taxiops: { icon: CHECK, note: 'One-click CSV from Compliance Tower' },
        dispatch: CROSS,
        manual: CROSS,
      },
    ],
  },
  {
    category: 'Live Dispatch & Fleet Visibility',
    items: [
      {
        title: 'Dispatcher calling drivers to find the nearest one',
        detail: 'Phone round-robin to assign a job. Slow, inconsistent, and leaves drivers waiting.',
        taxiops: { icon: CHECK, note: 'Geospatial auto-dispatch engine' },
        dispatch: PARTIAL,
        manual: CROSS,
      },
      {
        title: 'Job sits unassigned — no escalation logic',
        detail: 'Auto-dispatch offers one driver and stops. Next candidate only if manually reassigned.',
        taxiops: { icon: CHECK, note: '45-second timer, auto-escalates to next driver' },
        dispatch: PARTIAL,
        manual: CROSS,
      },
      {
        title: 'No real-time view of where the fleet is',
        detail: 'Dispatcher works blind. No idea who is free, who is on-trip, who has gone offline.',
        taxiops: { icon: CHECK, note: 'Live map, GPS every 12 seconds' },
        dispatch: PARTIAL,
        manual: CROSS,
      },
      {
        title: 'Driver works 14 hours — nobody notices',
        detail: 'HOS violations accumulate quietly. No system to flag or block until after the fact.',
        taxiops: { icon: CHECK, note: 'Progressive warnings, trips blocked at limit' },
        dispatch: CROSS,
        manual: CROSS,
      },
      {
        title: 'Trip goes cold — nobody closes it',
        detail: 'Meter running, driver unresponsive, booking permanently stuck in an open state.',
        taxiops: { icon: CHECK, note: 'Idle detection, auto-cancelled if ignored' },
        dispatch: CROSS,
        manual: CROSS,
      },
    ],
  },
  {
    category: 'Driver App & Trip Execution',
    items: [
      {
        title: 'Signal cuts out mid-trip — meter and GPS lost',
        detail: 'The trip disappears. Driver has nothing to show for the run. Revenue is unrecoverable.',
        taxiops: { icon: CHECK, note: 'Full offline operation, auto-sync on reconnect' },
        dispatch: CROSS,
        manual: CROSS,
      },
      {
        title: 'OS kills the app mid-trip',
        detail: 'Android aggressively terminates background apps. Driver loses trip state entirely.',
        taxiops: { icon: CHECK, note: 'Native background service, survives OS kill' },
        dispatch: CROSS,
        manual: CROSS,
      },
      {
        title: 'Passenger disputes the fare — no proof',
        detail: 'No GPS record. No meter log. Just a driver\'s word against a passenger\'s.',
        taxiops: { icon: CHECK, note: 'Full GPS trail and meter log on every booking' },
        dispatch: PARTIAL,
        manual: CROSS,
      },
      {
        title: 'Driver charges a fare the fleet never agreed to',
        detail: 'Uncontrolled fares. Flat rates invented on the spot. No consistency across the fleet.',
        taxiops: { icon: CHECK, note: 'Digital meter, admin-controlled flat rates' },
        dispatch: PARTIAL,
        manual: CROSS,
      },
      {
        title: 'New assignment not seen until driver unlocks the phone',
        detail: 'App in background, screen off — offer expires before driver even sees it.',
        taxiops: { icon: CHECK, note: 'Push to Android & iOS, screen off' },
        dispatch: PARTIAL,
        manual: CROSS,
      },
    ],
  },
  {
    category: 'Records, Safety & Retention',
    items: [
      {
        title: 'No record of why a driver was suspended',
        detail: 'Incident happened. Discipline applied. Nothing documented. Liability exposure.',
        taxiops: { icon: CHECK, note: 'Structured incident reports with photo attachments' },
        dispatch: PARTIAL,
        manual: CROSS,
      },
      {
        title: 'Safety briefing acknowledged verbally — no log',
        detail: 'Driver says they saw it. No timestamp. No proof for audits or investigations.',
        taxiops: { icon: CHECK, note: 'Acknowledgement required before app access' },
        dispatch: CROSS,
        manual: CROSS,
      },
      {
        title: 'Compliance team and dispatch use different driver data',
        detail: 'Two systems, two records. Expired compliance status not visible to the dispatcher assigning jobs.',
        taxiops: { icon: CHECK, note: 'Single shared driver record across both products' },
        dispatch: CROSS,
        manual: CROSS,
      },
      {
        title: 'Nothing motivating drivers to run more trips',
        detail: 'No feedback loop. High performers and low performers treated identically.',
        taxiops: { icon: CHECK, note: 'Weekly tiered performance messages' },
        dispatch: CROSS,
        manual: CROSS,
      },
    ],
  },
];

export default function ComparisonSection() {
  const [headerRef, headerInView] = useInView();
  const [tableRef, tableInView] = useInView();

  return (
    <section className="cmp" id="compare" aria-labelledby="cmp-heading">
      <div className="container">
        <div className="cmp__header" ref={headerRef}>
          <p className={`section-label fade-up ${headerInView ? 'in-view' : ''}`}>
            Why TaxiOps
          </p>
          <h2
            id="cmp-heading"
            className={`cmp__heading fade-up d1 ${headerInView ? 'in-view' : ''}`}
          >
            The problems it was<br />
            <span className="gradient-text">actually built to solve</span>
          </h2>
          <p className={`cmp__sub fade-up d2 ${headerInView ? 'in-view' : ''}`}>
            TaxiOps was designed from inside the transportation business, not as a
            feature checklist — but as a direct response to the operational failures
            that cost fleets time, revenue, and compliance standing every week.
          </p>
        </div>

        <div className={`cmp__table-wrap fade-up d3 ${tableInView ? 'in-view' : ''}`} ref={tableRef}>
          <div className="cmp__table" role="table" aria-label="Platform comparison">

            {/* Column headers */}
            <div className="cmp__row cmp__row--head" role="row">
              <div className="cmp__cell cmp__cell--feature" role="columnheader">
                Operational Problem
              </div>
              <div className="cmp__cell cmp__cell--taxiops" role="columnheader">
                <span className="cmp__col-name">TaxiOps</span>
              </div>
              <div className="cmp__cell cmp__cell--other" role="columnheader">
                Dispatch Software
              </div>
              <div className="cmp__cell cmp__cell--other" role="columnheader">
                Manual / Paper
              </div>
            </div>

            {ROWS.map((group) => (
              <div key={group.category} className="cmp__group" role="rowgroup">
                <div className="cmp__category-row" role="row">
                  <div className="cmp__category-label" role="cell">
                    {group.category}
                  </div>
                </div>
                {group.items.map((item) => (
                  <div key={item.title} className="cmp__row" role="row">
                    <div className="cmp__cell cmp__cell--feature" role="cell">
                      <span className="cmp__feat-title">{item.title}</span>
                      <span className="cmp__feat-detail">{item.detail}</span>
                    </div>
                    <div className="cmp__cell cmp__cell--taxiops" role="cell">
                      <div className="cmp__taxiops-val">
                        {item.taxiops.icon}
                        <span className="cmp__taxiops-note">{item.taxiops.note}</span>
                      </div>
                    </div>
                    <div className="cmp__cell cmp__cell--other" role="cell">
                      {item.dispatch}
                      {item.dispatch === PARTIAL && (
                        <span className="cmp__varies-label">Varies</span>
                      )}
                    </div>
                    <div className="cmp__cell cmp__cell--other" role="cell">
                      {item.manual}
                    </div>
                  </div>
                ))}
              </div>
            ))}

            {/* Legend */}
            <div className="cmp__legend">
              <span>{CHECK} Solved</span>
              <span>{PARTIAL} <span className="cmp__varies-label">Varies</span> — vendor-dependent</span>
              <span>{CROSS} Not addressed</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
